/**
 * Gestionnaire de loaders
 * @class Loader
 */
export default class Loader {
	/**
	 * Crée une instance de Loader
	 * @param {Object} [options={}] - Options de configuration
	 * @param {string} [options.type='spinner'] - Type de loader (spinner, dots, etc.)
	 * @param {string} [options.message=''] - Message par défaut
	 */
	constructor({ type = 'spinner', message = '' } = {}) {
		this.defaults = { type, message };
		this.loaders = new Map();
	}

	/**
	 * Initialise un loader
	 * @param {HTMLElement} element - L'élément du DOM
	 * @param {Object} [options={}] - Options de configuration
	 * @param {string} [options.type] - Type de loader
	 * @param {string} [options.message] - Message du loader
	 * @returns {Object} - Instance du loader
	 */
	init(element, options = {}) {
		if (this.loaders.has(element)) return this.loaders.get(element);

		const loader = {
			element,
			isVisible: false,
			type:
				options.type ||
				element.dataset.loaderType ||
				this.defaults.type,
			message:
				options.message ||
				element.dataset.loaderMessage ||
				this.defaults.message,

			/**
			 * Affiche le loader
			 * @param {string} [customMessage] - Message personnalisé
			 */
			show: (customMessage) => {
				if (customMessage) {
					const messageEl = element.querySelector('.loader__message');
					if (messageEl) {
						messageEl.textContent = customMessage;
					}
				}

				element.style.display = 'flex';
				element.classList.add('is-visible');
				loader.isVisible = true;
			},

			/**
			 * Masque le loader
			 */
			hide: () => {
				element.style.display = 'none';
				element.classList.remove('is-visible');
				loader.isVisible = false;
			},
		};

		// Initialisation du DOM
		element.classList.add('loader');
		element.innerHTML = `
            <div class="loader__wrapper">
                <div class="loader__${loader.type}">
                    ${loader.type === 'dots' ? '<span></span><span></span><span></span>' : ''}
					${loader.type === 'square' ? '<span></span><span></span><span></span>' : ''}
                </div>
                <div class="loader__message">${loader.message}</div>
            </div>
        `;

		// Masquer le loader après le chargement complet de la page
		window.addEventListener(
			'load',
			() => {
				loader.hide();
			},
			{ once: true }
		);

		// Stocker l'instance
		this.loaders.set(element, loader);

		return loader;
	}

	/**
	 * Récupère une instance de loader par son sélecteur
	 * @param {string|HTMLElement} selector - Sélecteur CSS ou élément DOM
	 * @returns {Object|Object[]} - Instance(s) du loader
	 */
	get(selector) {
		if (typeof selector === 'string') {
			const elements = document.querySelectorAll(selector);
			return elements.length === 1
				? this.loaders.get(elements[0]) || this.init(elements[0])
				: Array.from(elements).map(
						(el) => this.loaders.get(el) || this.init(el)
					);
		}
		return this.loaders.get(selector) || this.init(selector);
	}

	/**
	 * Affiche un ou plusieurs loaders
	 * @param {string|HTMLElement} [selector] - Sélecteur ou élément du loader (optionnel : tous)
	 * @param {string} [message] - Message optionnel
	 */
	show(selector, message) {
		if (!selector) {
			this.loaders.forEach((loader) => loader.show(message));
		} else {
			const targets =
				typeof selector === 'string'
					? document.querySelectorAll(selector)
					: [selector];
			targets.forEach((el) => {
				const loader = this.get(el);
				if (loader) loader.show(message);
			});
		}
	}

	/**
	 * Masque un ou plusieurs loaders
	 * @param {string|HTMLElement} [selector] - Sélecteur ou élément du loader (optionnel : tous)
	 */
	hide(selector) {
		if (!selector) {
			this.loaders.forEach((loader) => loader.hide());
		} else {
			const targets =
				typeof selector === 'string'
					? document.querySelectorAll(selector)
					: [selector];
			targets.forEach((el) => {
				const loader = this.get(el);
				if (loader) loader.hide();
			});
		}
	}
}

window.Loader = Loader;
